import {
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { from, Observable } from "rxjs";
import { switchMap } from "rxjs/operators";
import { AesCryptoService } from "src/services/aes-crypto.service";

export function aesEncryptionInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const aesCrypto = inject(AesCryptoService);

  if (!shouldUseEncryption(request, aesCrypto)) {
    return next(request);
  }

  const requestForTextResponse = request.clone({
    responseType: "text",
  }) as HttpRequest<unknown>;

  if (request.body === null || isFormData(request.body)) {
    return next(requestForTextResponse).pipe(
      switchMap((event) => decryptResponse(event, aesCrypto)),
    );
  }

  return from(aesCrypto.encrypt(request.body)).pipe(
    switchMap((encryptedBody) =>
      next(
        requestForTextResponse.clone({
          body: { data: encryptedBody },
          setHeaders: {
            "Content-Type": "application/json",
          },
        }),
      ),
    ),
    switchMap((event) => decryptResponse(event, aesCrypto)),
  );
}

function shouldUseEncryption(
  request: HttpRequest<unknown>,
  aesCrypto: AesCryptoService,
): boolean {
  return aesCrypto.isEnabled() && request.responseType === "json";
}

async function decryptResponse(
  event: HttpEvent<unknown>,
  aesCrypto: AesCryptoService,
): Promise<HttpEvent<unknown>> {
  if (!(event instanceof HttpResponse) || typeof event.body !== "string") {
    return event;
  }

  const encryptedResponse = getEncryptedResponseData(event.body);
  let responseText = encryptedResponse;

  try {
    responseText = await aesCrypto.decrypt(encryptedResponse);
  } catch {
    responseText = event.body;
  }

  return event.clone({ body: aesCrypto.parseDecryptedJson(responseText) });
}

function getEncryptedResponseData(responseBody: string): string {
  try {
    const parsedBody = JSON.parse(responseBody) as { data?: unknown };

    if (typeof parsedBody.data === "string") {
      return parsedBody.data;
    }
  } catch {
    return responseBody;
  }

  return responseBody;
}

function isFormData(value: unknown): value is FormData {
  return typeof FormData !== "undefined" && value instanceof FormData;
}
